import { hasOnlyIncompleteTariffs } from '@billing/validation/incompleteClaim';
//React
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Alert,
  Button,
  Card,
  Chip,
  Divider,
  Flex,
  LoadingOverlay,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { DateInput, TimePicker } from '@mantine/dates';
import { MRT_Row, MRT_TableInstance } from 'mantine-react-table';

import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';

import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { useCurrentSiteStore } from 'store/SiteStore';

// Icons
import {
  IconAlertTriangleFilled,
  IconEye,
  IconEyeClosed,
} from '@tabler/icons-react';

// Interfaces
import {
  Claim,
  CreateClaimForm,
  Healthcard,
  Patient,
  Payor,
  Provider,
} from 'interfaces';

import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

//Components
import {
  AllKeys,
  ClaimDetailSection,
  ClaimHealthcardSection,
  ClaimInfoSection,
  ClaimCommentSection,
  ClaimModalSubmitButton,
  ClaimPatientSection,
  ClaimProviderSection,
  ClaimTariffSection,
  PatientHistoryModal,
  SKClaimDetailSectionKeys,
  createDetailSectionKeys,
  claimCommentSectionKeys,
  createInfoSectionKeys,
  healthcardSectionKeys as healthcardKeys,
  patientSectionKeys as patientKeys,
  providerSectionKeys as providerKeys,
  tariffSectionKeys as tariffKeys,
  useActiveForm,
} from 'pages/claims/components';

import { useApplyLiveRuleData } from 'hooks/useApplyLiveRuleData';
import { useClaimRuleValidation } from 'hooks/useClaimRuleValidation';
import { useIsSKClaim } from 'hooks/useIsSKClaim';
import type { LiveClaimRulesState } from 'hooks/useLiveClaimRules';
import { useValidationSummaryFeedback } from 'hooks/useValidationSummaryFeedback';
import { getDuplicateSourceClaimID } from 'pages/claims/utils/claimRelationshipUtils';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  formatTimeInput,
  getDurationOrDefault,
  isFutureDate,
  nowISO,
  parseDateOnlyInputValue,
  parseDateTime,
  parseTimeInput,
  toJSDate,
} from 'utils/dateUtils';
import { buildRepeatedClaims } from 'utils/repeatClaimUtils';
import { v4 as uuidv4 } from 'uuid';
import { FutureServiceDateAlert } from './FutureServiceDateAlert';

interface ClaimCreateModalProps {
  table: MRT_TableInstance<Claim>;
  row: MRT_Row<Claim>;
  internalEditComponents?: React.ReactNode[];
  providers: Provider[];
  payors: Payor[];
  mode?: 'single' | 'repeat';
  liveRules?: LiveClaimRulesState;
  duplicateSourceClaim?: Claim | null;
}

export const ClaimCreateModal: React.FC<ClaimCreateModalProps> = ({
  table,
  row,
  internalEditComponents,
  providers,
  payors,
  mode = 'single',
  liveRules,
  duplicateSourceClaim,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const { translate } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(true);
  const [repeatDateRange, setRepeatDateRange] = useState<
    [string | null, string | null]
  >([null, null]);
  const [excludedDates, setExcludedDates] = useState<string[]>([]);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [repeatStartDateError, setRepeatStartDateError] = useState<
    string | null
  >(null);
  const [repeatEndDateError, setRepeatEndDateError] = useState<string | null>(
    null
  );
  const {
    animateValidationFeedback,
    clearValidationFeedback,
    revealValidationSummary,
    showValidationFeedback,
    validationSummaryRef,
  } = useValidationSummaryFeedback();

  const { form } = useActiveForm();
  const isIncomplete = hasOnlyIncompleteTariffs(form.getValues());
  const isSKClaim = useIsSKClaim(form);
  const previousFormValuesRef = React.useRef(form.values);
  const { clearCreateDraft } = useClaimDraftStore();
  const index = row.id;

  const { organization, timezone } = useCurrentSiteStore();

  const [showPatientHistory, setShowPatientHistory] = useState(true);
  const [isNewPatient, setIsNewPatient] = useState<boolean>(false);
  const patientID = form.getValues().patientID;
  const duplicateSourceClaimID = getDuplicateSourceClaimID({
    sourceClaim: duplicateSourceClaim,
    patientID: form.values.patientID,
    siteID: form.values.siteID,
  });
  const isRepeatMode = mode === 'repeat';
  const {
    clearServerRuleValidation,
    hasCurrentBlockingRules,
    hasCurrentLiveBlockingRules,
    liveBlockingRuleMessages,
    liveRuleError,
    liveRuleWarningMessages,
    isEvaluating,
    serverRuleErrors,
    setServerRuleFailure,
  } = useClaimRuleValidation(form, liveRules, isIncomplete);
  useApplyLiveRuleData(form, liveRules);
  const formErrorEntries = Object.entries(form.errors).filter(([, message]) =>
    Boolean(message)
  );
  const displayedServerRuleErrors = serverRuleErrors.map((message) =>
    /^validation failed\.?$/i.test(message.trim())
      ? translate(
          'pages:claims.modals.claimValidationFailedMessage',
          'The claim contains missing or invalid information. Review the highlighted fields and try again.'
        )
      : message
  );

  useEffect(() => {
    if (hasCurrentBlockingRules) setShowErrors(true);
  }, [hasCurrentBlockingRules]);

  useEffect(() => {
    if (formErrorEntries.length === 0 && !hasCurrentBlockingRules) {
      clearValidationFeedback();
    }
  }, [
    clearValidationFeedback,
    formErrorEntries.length,
    hasCurrentBlockingRules,
  ]);

  useEffect(() => {
    if (hasCurrentLiveBlockingRules) {
      setShowErrors(true);
      revealValidationSummary();
    }
  }, [hasCurrentLiveBlockingRules, revealValidationSummary]);

  useEffect(() => {
    if (previousFormValuesRef.current === form.values) return;
    previousFormValuesRef.current = form.values;

    if (isRepeatMode) {
      clearServerRuleValidation();
    }
  }, [clearServerRuleValidation, form.values, isRepeatMode]);

  form.watch('payorID', ({ previousValue, value }) => {
    if (previousValue === value) {
      return;
    }

    if (!value) {
      form.setFieldValue('payor', null);
      return;
    }

    const payor = payors.find((payor) => payor.id === value);
    if (payor) {
      form.setFieldValue('payor', payor);
    }
  });

  const getIsoDatesFromRange = (
    range: [string | null, string | null]
  ): string[] => {
    const [start, end] = range;
    if (!start) return [];
    const startDay = DateTime.fromISO(start).startOf('day');
    const endDate = end || start;
    const endDay = DateTime.fromISO(endDate).startOf('day');
    if (!startDay.isValid || !endDay.isValid || endDay < startDay) return [];

    const days: string[] = [];
    for (
      let current = startDay;
      current <= endDay;
      current = current.plus({ days: 1 })
    ) {
      days.push(current.toISODate());
    }
    return days;
  };

  const rangeIsoDates = useMemo(
    () => getIsoDatesFromRange(repeatDateRange),
    [repeatDateRange, timezone]
  );

  const excludedDateSet = useMemo(
    () => new Set(excludedDates),
    [excludedDates]
  );

  const repeatDates = useMemo(
    () => rangeIsoDates.filter((date) => !excludedDateSet.has(date)),
    [rangeIsoDates, excludedDateSet]
  );

  const repeatCount = repeatDates.length;
  const futureServiceStarts =
    isRepeatMode && repeatDates.length > 0
      ? repeatDates
      : [form.values?.serviceStart];
  const hasFutureServiceStart = futureServiceStarts.some((serviceStart) =>
    isFutureDate(serviceStart, timezone)
  );

  const handleRepeatRangeChange = (value: [string | null, string | null]) => {
    setRepeatDateRange(value);
    setRepeatError(null);
    const allowed = new Set(getIsoDatesFromRange(value));
    setExcludedDates((prev) => prev.filter((date) => allowed.has(date)));
  };

  const parseRepeatDate = (value: string) => {
    const parsed = parseDateOnlyInputValue(value, timezone);
    return parsed.isValid ? parsed.toISODate() : null;
  };

  const invalidRepeatDateMessage = translate(
    'pages:claims.repeatDates.invalidDateFormat',
    'Use YYYY-MM-DD or YYYY MM DD.'
  );

  const toggleDateInclusion = (isoDate: string) => {
    setExcludedDates((prev) => {
      const next = new Set(prev);
      if (next.has(isoDate)) {
        next.delete(isoDate);
      } else {
        next.add(isoDate);
      }
      return Array.from(next);
    });
    setRepeatError(null);
  };

  const serviceStartTime = useMemo(
    () => formatTimeInput(form.values?.serviceStart, timezone),
    [form.values?.serviceStart, timezone]
  );

  const serviceEndTime = useMemo(
    () => formatTimeInput(form.values?.serviceEnd, timezone),
    [form.values?.serviceEnd, timezone]
  );

  const handleTimeChange = (
    value: string,
    field: 'serviceStart' | 'serviceEnd'
  ) => {
    const parsed = parseTimeInput(value);
    if (!parsed.isValid) return;

    const baseDate = repeatDateRange[0] ?? form.values?.serviceStart;
    if (!baseDate) return;
    const baseDay = parseDateTime(baseDate, { zone: timezone }).startOf('day');
    if (!baseDay.isValid) return;
    const next = baseDay.set({
      hour: parsed.hour,
      minute: parsed.minute,
    });
    const baseValues = form.getValues();
    form.setFieldValue(field, next.toISO());
    if (field === 'serviceStart') {
      const duration = getDurationOrDefault(
        baseValues.serviceStart,
        baseValues.serviceEnd
      );
      const nextEnd = next.plus(duration);
      form.setFieldValue('serviceEnd', nextEnd.toISO());
    }

    const startTime =
      field === 'serviceStart'
        ? next
        : parseDateTime(form.values?.serviceStart, { zone: timezone });
    const endTime =
      field === 'serviceEnd'
        ? next
        : parseDateTime(form.values?.serviceEnd, { zone: timezone });
    if (startTime.isValid && endTime.isValid && endTime <= startTime) {
      setRepeatError('Service end time must be after start time');
      return;
    }

    setRepeatError(null);
  };

  useEffect(() => {
    const patientID = form.getValues().patientID;
    if (patientID && patientID.includes('new-patient')) {
      setIsNewPatient(true);
    }
  }, [patientID]);

  useEffect(() => {
    if (isNewPatient) {
      const newPatientID = uuidv4();
      const healthcardID = uuidv4();
      const patientData = form.getValues().patient as Patient | null;
      const newPatient: Patient = {
        id: newPatientID,
        firstName: patientData?.firstName || '',
        lastName: patientData?.lastName || '',
        address: '',
        city: '',
        country: '',
        dateOfBirth: nowISO(),
        maritalStatus: '',
        postalCode: '',
        province: null,
        sex: null,
        healthcardID: healthcardID,
        email: null,
        healthcards: [],
        phone: null,
        searchVector: null,
        organization,
        organizationID: organization.id,
      };
      const newHealthcard: Healthcard = {
        number: '',
        registration: '',
        province: null,
        id: healthcardID,
        patientID: newPatientID,
      };
      form.setFieldValue('patient', newPatient);
      form.setFieldValue('healthcard', newHealthcard);
      form.setFieldValue('patientID', newPatientID);
      form.setFieldValue('healthcardID', healthcardID);
      form.setFieldValue('isNewPatient', true);
    }
  }, [isNewPatient]);

  const editComponentsMap = useMemo(() => {
    const map: Record<string, ReactNode> = {};
    internalEditComponents.forEach((comp: ReactElement) => {
      if (comp.key) map[comp.key as string] = comp;
    });
    return map;
  }, [internalEditComponents]);

  const handleOnClick = async (): Promise<boolean> => {
    setIsLoading(true);
    clearServerRuleValidation();
    try {
      if (
        !hasOnlyIncompleteTariffs(form.getValues()) &&
        (isEvaluating || hasCurrentBlockingRules)
      ) {
        setShowErrors(true);
        revealValidationSummary();
        setIsLoading(false);
        return false;
      }
      // prevent form submission if form is invalid
      if (!form.isValid()) {
        form.validate();
        setShowErrors(true);
        revealValidationSummary();
        setIsLoading(false);
        return false;
      }

      if (isRepeatMode) {
        const startDate = repeatDateRange[0];
        const endDate = repeatDateRange[1] ?? repeatDateRange[0];
        const startDay =
          startDate &&
          parseDateTime(startDate, { zone: timezone }).startOf('day');
        const endDay =
          endDate && parseDateTime(endDate, { zone: timezone }).startOf('day');

        if (!startDay) {
          setRepeatError('Select a service date or date range to repeat.');
          setIsLoading(false);
          return false;
        }
        if (!startDay.isValid || !endDay?.isValid || endDay < startDay) {
          setRepeatError(
            'End date must be the same as or after the start date.'
          );
          setIsLoading(false);
          return false;
        }

        if (repeatCount === 0) {
          setRepeatError('At least one service date must be selected.');
          setIsLoading(false);
          return false;
        }

        setRepeatError(null);
        const repeatedClaims = buildRepeatedClaims(
          form?.getValues() as CreateClaimForm,
          toJSDate(startDay),
          toJSDate(endDay),
          { excludedDates }
        );

        if (repeatedClaims.length === 0) {
          setRepeatError('Could not build repeat claims for that date range.');
          setIsLoading(false);
          return false;
        }

        row._valuesCache = repeatedClaims as unknown as Record<
          string,
          CreateClaimForm
        >;
        await table.options.onCreatingRowSave?.({
          exitCreatingMode: () => table.setCreatingRow(null),
          row,
          table,
          values: repeatedClaims as unknown as Record<string, CreateClaimForm>,
        });
        clearCreateDraft();
        return true;
      } else {
        const createValues = form?.getValues() as CreateClaimForm;
        const valuesWithRelationship = {
          ...createValues,
          ...(duplicateSourceClaimID ? { duplicateSourceClaimID } : {}),
        };
        row._valuesCache = valuesWithRelationship as unknown as Record<
          string,
          CreateClaimForm
        >;
        await table.options.onCreatingRowSave?.({
          exitCreatingMode: () => table.setCreatingRow(null),
          row,
          table,
          values: valuesWithRelationship as unknown as Record<
            string,
            CreateClaimForm
          >,
        });
        clearCreateDraft();
        return true;
      }
    } catch (e) {
      if (setServerRuleFailure(e)) {
        setShowErrors(true);
        revealValidationSummary();
      }
      Sentry.captureException(e);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAsIncomplete = async () => {
    const values = form.getValues() as CreateClaimForm;
    const originalStatuses = new Map(
      (values.tariffs ?? []).map((tariff) => [tariff.id, tariff.status])
    );
    form.setFieldValue(
      'tariffs',
      (values.tariffs ?? []).map((tariff) => ({
        ...tariff,
        status: CLAIM_TARIFF_STATUS.INCOMPLETE,
      }))
    );
    const saved = await handleOnClick();
    if (!saved) {
      form.setFieldValue(
        'tariffs',
        (form.getValues().tariffs ?? []).map((tariff) => ({
          ...tariff,
          status: originalStatuses.get(tariff.id) ?? tariff.status,
        }))
      );
    }
  };

  const infoSectionKeys: AllKeys[] = useMemo(
    () =>
      isRepeatMode
        ? createInfoSectionKeys.filter(
            (key) => key !== 'serviceStart' && key !== 'serviceEnd'
          )
        : createInfoSectionKeys,
    [isRepeatMode]
  );

  const detailSectionKeys: AllKeys[] = useMemo(
    () => [
      ...createDetailSectionKeys.filter(
        (key) => !claimCommentSectionKeys.includes(key)
      ),
      ...(isSKClaim ? SKClaimDetailSectionKeys : []),
    ],
    [isSKClaim]
  );

  const patientSectionKeys: AllKeys[] = useMemo(() => patientKeys, []);

  const healthcardSectionKeys: AllKeys[] = useMemo(() => healthcardKeys, []);

  const providerSectionKeys: AllKeys[] = useMemo(() => providerKeys, []);

  const tariffSectionKeys: AllKeys[] = useMemo(() => tariffKeys, []);

  return (
    <React.Fragment>
      <LoadingOverlay visible={isLoading} />
      <Text size="sm" c="dimmed" ta="center" aria-hidden={!isEvaluating}>
        {isEvaluating
          ? translate(
              'pages:claims.modals.rulesChecking',
              'Checking billing rules…'
            )
          : '\u00a0'}
      </Text>

      <Stack
        p={'lg'}
        align="stretch"
        justify="center"
        gap={'lg'}
        data-testid="create-claim-modal"
      >
        <Card shadow="lg" radius="md" padding={'xl'} withBorder>
          <Stack gap={'lg'}>
            <FutureServiceDateAlert
              serviceStarts={futureServiceStarts}
              timezone={timezone}
            />
            <div
              ref={validationSummaryRef}
              data-testid="claim-validation-summary"
              tabIndex={-1}
              style={{ scrollMarginTop: 'var(--mantine-spacing-md)' }}
            />
            {showErrors && formErrorEntries.length > 0 && (
              <Flex
                justify="center"
                align="stretch"
                gap="md"
                data-testid={'edit-claim-alert'}
              >
                <Alert
                  icon={<IconAlertTriangleFilled size={22} />}
                  title={translate(
                    'pages:claims.modals.formValidationTitle',
                    'Fix the highlighted fields'
                  )}
                  color="red"
                  radius="sm"
                  variant="light"
                  w="100%"
                  style={{ border: '2px solid var(--mantine-color-red-6)' }}
                >
                  <Text fw={600} mb="xs">
                    {translate(
                      'pages:claims.modals.formValidationMessage',
                      { count: formErrorEntries.length },
                      formErrorEntries.length === 1
                        ? 'This claim has 1 error. Correct it before creating the claim.'
                        : `This claim has ${formErrorEntries.length} errors. Correct them before creating the claim.`
                    )}
                  </Text>
                  <Stack component="ul" gap={4} m={0} pl="lg">
                    {formErrorEntries.map(([key, message]) => (
                      <Text component="li" key={key} size="sm">
                        {key}: {message}
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              </Flex>
            )}
            {serverRuleErrors.length > 0 && (
              <Flex justify={'center'} align={'stretch'} gap={'md'}>
                <Alert
                  icon={<IconAlertTriangleFilled size={22} />}
                  title={translate(
                    'pages:claims.modals.ruleValidationFailedTitle',
                    'This claim can’t be saved yet'
                  )}
                  color="red"
                  radius="sm"
                  variant="light"
                  w="100%"
                  style={{ border: '2px solid var(--mantine-color-red-6)' }}
                >
                  {displayedServerRuleErrors.map((message) => (
                    <Text key={message}>{message}</Text>
                  ))}
                </Alert>
              </Flex>
            )}
            {liveBlockingRuleMessages.length > 0 &&
              serverRuleErrors.length === 0 && (
                <Flex justify="center" align="stretch" gap="md">
                  <Alert
                    icon={<IconAlertTriangleFilled size={22} />}
                    title={translate(
                      'pages:claims.modals.ruleValidationFailedTitle',
                      'This claim can’t be saved yet'
                    )}
                    color="red"
                    radius="sm"
                    variant="light"
                    w="100%"
                    style={{ border: '2px solid var(--mantine-color-red-6)' }}
                  >
                    {liveBlockingRuleMessages.map((message) => (
                      <Text key={message}>{message}</Text>
                    ))}
                  </Alert>
                </Flex>
              )}
            {liveRuleWarningMessages.length > 0 && (
              <Flex justify="center" align="stretch" gap="md">
                <Alert
                  title={translate(
                    'pages:claims.modals.ruleWarningsTitle',
                    'Billing rule warnings'
                  )}
                  color="yellow"
                  radius="sm"
                >
                  {liveRuleWarningMessages.map((message) => (
                    <Text key={message}>{message}</Text>
                  ))}
                </Alert>
              </Flex>
            )}
            {liveRuleError && !isEvaluating && (
              <Alert
                title={translate(
                  'pages:claims.modals.rulesUnavailableTitle',
                  'Rule preview unavailable'
                )}
                color="yellow"
                radius="sm"
              >
                {translate(
                  'pages:claims.modals.rulesUnavailableMessage',
                  'Rules will be checked again when you save.'
                )}
              </Alert>
            )}
            <ClaimProviderSection
              index={index}
              providers={providers}
              title="Provider"
              keys={providerSectionKeys}
              cols={4}
              editComponentsMap={editComponentsMap}
              type="create"
            />
            {isRepeatMode && (
              <Card
                shadow="xs"
                radius="md"
                padding="md"
                withBorder
                style={{ borderColor: repeatError ? 'red' : 'green' }}
              >
                <Stack gap="xs">
                  <Text fw={600}>Repeat Service Dates</Text>
                  <Text size="sm" c="dimmed">
                    Select a service date range to create one claim per day.
                    Adjust the service times below; they will be applied to
                    every included date.
                  </Text>
                  <Flex gap="md" wrap="wrap">
                    <DateInput
                      label={translate(
                        'pages:claims.repeatDates.startDate',
                        'Start Date'
                      )}
                      placeholder="YYYY-MM-DD"
                      value={repeatDateRange[0]}
                      onChange={(value) => {
                        setRepeatStartDateError(null);
                        handleRepeatRangeChange([
                          value,
                          value ? repeatDateRange[1] : null,
                        ]);
                      }}
                      onBlur={(event) => {
                        const value = event.currentTarget.value.trim();
                        setRepeatStartDateError(
                          value && !parseRepeatDate(value)
                            ? invalidRepeatDateMessage
                            : null
                        );
                      }}
                      dateParser={parseRepeatDate}
                      valueFormat="YYYY-MM-DD"
                      error={repeatStartDateError}
                      clearable
                      clearButtonProps={{ tabIndex: -1 }}
                      radius="md"
                      w={240}
                    />
                    <DateInput
                      label={translate(
                        'pages:claims.repeatDates.endDate',
                        'End Date'
                      )}
                      placeholder="YYYY-MM-DD"
                      value={repeatDateRange[1]}
                      onChange={(value) => {
                        setRepeatEndDateError(null);
                        handleRepeatRangeChange([repeatDateRange[0], value]);
                      }}
                      onBlur={(event) => {
                        const value = event.currentTarget.value.trim();
                        setRepeatEndDateError(
                          value && !parseRepeatDate(value)
                            ? invalidRepeatDateMessage
                            : null
                        );
                      }}
                      dateParser={parseRepeatDate}
                      valueFormat="YYYY-MM-DD"
                      error={repeatEndDateError}
                      clearable
                      clearButtonProps={{ tabIndex: -1 }}
                      radius="md"
                      w={240}
                    />
                  </Flex>
                  {rangeIsoDates.length > 0 && (
                    <Stack gap={6}>
                      <Text size="sm" c="dimmed">
                        Click to deselect specific dates from the range.
                      </Text>
                      <Flex gap="xs" wrap="wrap">
                        {rangeIsoDates.map((isoDate) => {
                          const isExcluded = excludedDateSet.has(isoDate);
                          const label =
                            parseDateTime(isoDate).toFormat('ccc, MMM d');
                          return (
                            <Chip
                              key={isoDate}
                              checked={!isExcluded}
                              onChange={() => toggleDateInclusion(isoDate)}
                              variant={isExcluded ? 'outline' : 'filled'}
                              color={isExcluded ? 'gray' : 'blue'}
                              size="sm"
                              tabIndex={-1}
                            >
                              {label}
                            </Chip>
                          );
                        })}
                      </Flex>
                    </Stack>
                  )}
                  <Flex gap="md" wrap="wrap">
                    <TimePicker
                      label="Service Start Time"
                      value={serviceStartTime}
                      onChange={(value) =>
                        handleTimeChange(value, 'serviceStart')
                      }
                      radius="md"
                      size="md"
                      format="24h"
                      w={240}
                    />
                    <TimePicker
                      label="Service End Time"
                      value={serviceEndTime}
                      onChange={(value) =>
                        handleTimeChange(value, 'serviceEnd')
                      }
                      radius="md"
                      size="md"
                      format="24h"
                      w={240}
                    />
                  </Flex>
                  {repeatCount > 0 && (
                    <Text size="sm" c="dimmed">
                      {repeatCount} claim{repeatCount === 1 ? '' : 's'} will be
                      created
                      {excludedDates.length > 0
                        ? ` (${excludedDates.length} date${
                            excludedDates.length === 1 ? '' : 's'
                          } deselected)`
                        : ''}
                      .
                    </Text>
                  )}
                  {repeatError && (
                    <Alert
                      title="Missing service dates"
                      color="red"
                      radius="sm"
                    >
                      {repeatError}
                    </Alert>
                  )}
                </Stack>
              </Card>
            )}
            <ClaimInfoSection
              index={index}
              title="Claim Info"
              keys={infoSectionKeys}
              cols={6}
              editComponentsMap={editComponentsMap}
              type="create"
            />
            <Divider variant="dotted" />
            <ClaimDetailSection
              index={index}
              title="Claim Details"
              keys={detailSectionKeys}
              cols={4}
              editComponentsMap={editComponentsMap}
              type="create"
            />
            <Divider variant="dotted" />
            <ClaimPatientSection
              index={index}
              title="Patient"
              keys={patientSectionKeys}
              cols={3}
              editComponentsMap={editComponentsMap}
              type="create"
              isNewPatient={isNewPatient}
              setIsNewPatient={setIsNewPatient}
            />
            <ClaimHealthcardSection
              index={index}
              title="Healthcard"
              keys={healthcardSectionKeys}
              cols={3}
              editComponentsMap={editComponentsMap}
              type="create"
            />
            <Divider variant="dotted" />

            <Divider variant="dotted" />
            <ClaimTariffSection
              index={index}
              title="Tariffs"
              keys={tariffSectionKeys}
              cols={1}
              editComponentsMap={editComponentsMap}
              type="create"
            />
            <Divider variant="dotted" />
            <ClaimCommentSection
              index={index}
              title={translate(
                'pages:claims.comments.internalTitle',
                'Internal Comments'
              )}
              keys={claimCommentSectionKeys}
              cols={1}
              editComponentsMap={editComponentsMap}
              type="create"
              compact
              expanded={commentsExpanded}
              onExpandedChange={setCommentsExpanded}
              description={translate(
                isRepeatMode
                  ? 'pages:claims.comments.repeatCreateHelp'
                  : 'pages:claims.comments.createHelp',
                isRepeatMode
                  ? 'A separate comment will be added to each repeated claim.'
                  : 'This comment will be added when the claim is created.'
              )}
            />
          </Stack>
        </Card>
        <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
          <Button
            variant="outline"
            radius="md"
            color="gray"
            c={buttonTextColor(computedColorScheme, false)}
            onClick={() => table.setCreatingRow(null)}
          >
            Cancel
          </Button>
          <ClaimModalSubmitButton
            table={table}
            computedColorScheme={computedColorScheme}
            buttonText={
              isRepeatMode
                ? `${translate('buttons.create')} ${repeatCount} ${translate(
                    'pages:claims.label'
                  )}`
                : translate('buttons.create')
            }
            isLoading={isLoading}
            handleOnClick={handleOnClick}
            disabled={hasFutureServiceStart || isEvaluating}
            menuDisabled={hasFutureServiceStart || isLoading}
            disabledReason={
              isEvaluating
                ? translate(
                    'pages:claims.modals.rulesChecking',
                    'Checking billing rules…'
                  )
                : hasCurrentBlockingRules
                ? translate(
                    'pages:claims.modals.rulesBlockingSave',
                    'Resolve the billing rule errors before saving.'
                  )
                : 'A claim with a future service date cannot be submitted.'
            }
            handleCreateAsIncomplete={
              isRepeatMode ? undefined : handleCreateAsIncomplete
            }
            showValidationFeedback={showValidationFeedback}
            validationAnimationActive={animateValidationFeedback}
          />
        </Flex>
        {patientID && (
          <Card shadow="lg" radius="md" padding="xl" withBorder mt="md">
            <Button
              variant="outline"
              radius="md"
              w={'30%'}
              style={{ alignSelf: 'center' }}
              color="gray"
              onClick={() => setShowPatientHistory((prev) => !prev)}
            >
              {showPatientHistory ? (
                <Flex align="center" gap="sm">
                  <Text>Hide Patient History</Text>
                  <IconEyeClosed />
                </Flex>
              ) : (
                <Flex align="center" gap="sm">
                  <Text>View Patient History</Text>
                  <IconEye />
                </Flex>
              )}
            </Button>
            {showPatientHistory && (
              <PatientHistoryModal
                variant="inline"
                initialPatientID={patientID}
                initialPatient={form.getValues().patient as Patient | null}
                initialHealthcard={form.getValues().healthcard}
                payors={payors}
              />
            )}
          </Card>
        )}
      </Stack>
    </React.Fragment>
  );
};
